// File: frontend/src/App.js

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './AuthContext';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { ThemeProvider } from './ThemeContext';
import LanguageToggle from './components/LanguageToggle';
import ThemeToggle from './components/ThemeToggle';
import MobileBottomNav from './components/MobileBottomNav';
import StudentBottomNav from './components/StudentBottomNav';

// Import Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import DashboardPage from './pages/DashboardPage';
import AttendancePage from './pages/AttendancePage';
import ClassesPage from './pages/ClassesPage'; 
import StudentsPage from './pages/StudentsPage'; 
import ReportsPage from './pages/ReportsPage'; 
import ClassDetailPage from './pages/ClassDetailPage';
import CalendarPage from './pages/CalendarPage';
import RoomsPage from './pages/RoomsPage';
import PayrollPage from './pages/PayrollPage';
import LedgerPage from './pages/LedgerPage';
import StudentLoginPage from './pages/StudentLoginPage';
import StudentDashboardPage from './pages/StudentDashboardPage';
import StudentMaterialsPage from './pages/StudentMaterialsPage';
import StudentPaymentsPage from './pages/StudentPaymentsPage';
import StudentAttendanceHistoryPage from './pages/StudentAttendanceHistoryPage';
import StudentProfilePage from './pages/StudentProfilePage';
import MyExpensesPage from './pages/MyExpensesPage';
import AdminExpensesPage from './pages/AdminExpensesPage';
import LeaveRequestsPage from './pages/LeaveRequestsPage';
import StudentLeavePage from './pages/StudentLeavePage';

// Import Admin Components
import UserManagement from './components/Admin/UserManagement';
import RoomsManagement from './components/Admin/RoomsManagement';
import BankAccountManagement from './components/Admin/BankAccountManagement';
import LineServicesCard from './components/Admin/LineServicesCard';
import FlaggedNotesManagement from './components/Admin/FlaggedNotesManagement';

// --- Wrapper Components for Route Protection ---
function StaffLayoutWrapper() {
    const { currentUser } = useAuth();
    const isStaff = currentUser && ['admin', 'teacher', 'accountant'].includes(currentUser.role);
    if (!isStaff) return <Navigate to="/login" replace />;
    return <MainLayout />;
}

function StudentLayoutWrapper() {
    const { currentUser } = useAuth();
    const isStudent = currentUser && currentUser.role === 'student';
    if (!isStudent) return <Navigate to="/student-login" replace />;
    return <StudentLayout />;
}

function ExpensesPageWrapper() {
    const { currentUser } = useAuth();
    if (!currentUser) return null;
    return currentUser.role === 'teacher' ? <MyExpensesPage /> : <AdminExpensesPage />;
}

// The new layout for the Student Portal
function StudentLayout() {
    const { currentUser, logout } = useAuth();
    const { t } = useLanguage();

    useEffect(() => {
        console.log('[StudentLayout] Successfully rendered for user:', currentUser);
    }, [currentUser]);

    if (!currentUser) return null;
    const activeLinkStyle = { color: 'white', textDecoration: 'underline', fontWeight: 700 };

    return (
        <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
            <nav className="bg-green-600 text-white px-2 sm:px-4 lg:px-6 py-3 sm:py-3.5 shadow-md">
                <div className="w-full max-w-[1920px] mx-auto flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-4">
                    <div className="flex items-center justify-between sm:justify-start gap-3">
                        <Link to="/student/dashboard" className="text-xl sm:text-2xl font-bold flex items-center gap-2 hover:opacity-90 transition-opacity">
                            <img src="/logo192.png" alt="Irie Roots Logo" className="w-8 h-8 rounded-lg object-cover shadow-xs border border-white/20" />
                            <span>{t('nav_portal')}</span>
                        </Link>
                        <div className="flex items-center gap-2 sm:hidden">
                            <ThemeToggle />
                            <LanguageToggle />
                            <button onClick={logout} className="bg-green-700 hover:bg-green-800 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors">
                                {t('common_logout')}
                            </button>
                        </div>
                    </div>
                    <div className="hidden sm:flex overflow-x-auto no-scrollbar space-x-3 text-xs sm:text-sm font-medium pb-1 sm:pb-0 -mx-3 px-3 sm:mx-0 sm:px-0">
                        <NavLink to="/student/dashboard" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className="whitespace-nowrap py-1">{t('nav_dashboard')}</NavLink>
                        <NavLink to="/student/attendance" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className="whitespace-nowrap py-1">{t('nav_attendance')}</NavLink>
                        <NavLink to="/student/leave" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className="whitespace-nowrap py-1">{t('nav_leave')}</NavLink>
                        <NavLink to="/student/materials" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className="whitespace-nowrap py-1">{t('nav_materials')}</NavLink>
                        <NavLink to="/student/payments" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className="whitespace-nowrap py-1">{t('nav_payments')}</NavLink>
                        <NavLink to="/student/profile" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className="whitespace-nowrap py-1">{t('nav_profile')}</NavLink>
                    </div>
                    <div className="hidden sm:flex items-center space-x-3 text-sm">
                        <ThemeToggle />
                        <LanguageToggle />
                        <span>{t('common_welcome')}, {currentUser.name}!</span>
                        <button onClick={logout} className="bg-green-700 hover:bg-green-800 px-3.5 py-1.5 rounded-lg font-semibold text-xs transition-colors cursor-pointer">{t('common_logout')}</button>
                    </div>
                </div>
            </nav>
            <main className="w-full max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6 pb-24 sm:pb-6">
                <Outlet />
            </main>

            <StudentBottomNav />
        </div>
    );
}

// This is the main layout for the application AFTER a user logs in.
// It includes the navbar and the tabbed navigation.
function MainLayout() {
  const { currentUser, logout } = useAuth();
  const { t } = useLanguage();
  // Collapsed by default on mobile (it's 5 stacked widgets pushing the
  // actual page content far down the screen); open by default on
  // desktop/tablet where there's room for it.
  const [isAdminOpen, setIsAdminOpen] = React.useState(() => typeof window !== 'undefined' && window.innerWidth >= 640);

  // --- DIAGNOSTIC 2: Check the user once inside the layout ---
  useEffect(() => {
    console.log('[MainLayout] Component has rendered. currentUser:', currentUser);
  }, [currentUser]);

  if (!currentUser){
    console.log("[MainLayout] Rendering 'Please log in' because currentUser is null.");
    return <div>Please log in.</div>;
  }

  // Style for active NavLink tab
  const activeLinkStyle = {
    borderColor: '#2563EB', // blue-600
    color: '#2563EB',
    fontWeight: 700,
    backgroundColor: '#EFF6FF', // blue-50
  };

  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
      <nav className="bg-blue-600 text-white px-2 sm:px-4 lg:px-6 py-3 sm:py-3.5 shadow-md">
        <div className="w-full max-w-[1920px] mx-auto flex justify-between items-center gap-2">
          <Link to="/" className="text-lg sm:text-2xl font-bold hover:opacity-90 transition-opacity flex items-center gap-2.5">
            <img src="/logo192.png" alt="Irie Roots Logo" className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg object-cover shadow-xs border border-white/20" />
            <span className="hidden sm:inline">Irie Roots Attendance</span>
            <span className="sm:hidden">Irie Roots</span>
          </Link>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <ThemeToggle />
            <LanguageToggle />
            <NavLink to="/profile" className="hover:underline text-xs sm:text-sm font-medium truncate max-w-[120px] sm:max-w-none">
                {currentUser.username}
            </NavLink>
            <button onClick={logout} className="bg-blue-700 hover:bg-blue-800 px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors cursor-pointer">
                {t('common_logout')}
            </button>
          </div>
        </div>
      </nav>
      
      <main className="w-full max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6 pb-24 sm:pb-6">
        {currentUser.role === 'admin' && (
          <div id="admin-panel" className="mb-4 sm:mb-6 bg-white dark:bg-gray-800 p-3.5 sm:p-5 rounded-2xl shadow-xs border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3 cursor-pointer select-none" onClick={() => setIsAdminOpen(!isAdminOpen)}>
              <h2 className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <span>⚙️</span> {t('admin_controls')}
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 dark:text-gray-500 font-normal hidden md:inline">{t('admin_desc')}</span>
                <button 
                  type="button" 
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 px-2 py-1 bg-blue-50 rounded-md cursor-pointer"
                >
                  {isAdminOpen ? t('admin_collapse') : t('admin_expand')}
                </button>
              </div>
            </div>
            {isAdminOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                <UserManagement />
                <RoomsManagement />
                <BankAccountManagement />
                <LineServicesCard />
                <FlaggedNotesManagement />
              </div>
            )}
          </div>
        )}

        {/* Navigation Tabs - desktop/tablet only; mobile uses the bottom tab bar instead */}
        <div className="hidden sm:flex border-b mb-4 sm:mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-xs p-1.5 overflow-x-auto no-scrollbar -mx-2 px-2 sm:mx-0 sm:px-1.5 gap-1">
            <NavLink to="/dashboard" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_dashboard')}</NavLink>
            <NavLink to="/calendar" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_calendar')}</NavLink>
            <NavLink to="/attendance" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_attendance')}</NavLink>
            <NavLink to="/leave-requests" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_leave')}</NavLink>
            <NavLink to="/classes" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_classes')}</NavLink>
            <NavLink to="/students" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_students')}</NavLink>
            <NavLink to="/reports" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_reports')}</NavLink>
            <NavLink to="/expenses" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_expenses')}</NavLink>
            {(currentUser.role === 'admin' || currentUser.role === 'accountant' || currentUser.role === 'teacher') && (
              <NavLink to="/payroll" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_payroll')}</NavLink>
            )}
            {(currentUser.role === 'admin' || currentUser.role === 'accountant') && (
              <NavLink to="/ledger" className="py-2 px-3 sm:px-4 text-gray-600 dark:text-gray-400 hover:text-blue-600 rounded-lg text-xs sm:text-sm transition-colors whitespace-nowrap" style={({ isActive }) => isActive ? activeLinkStyle : undefined}>{t('nav_ledger')}</NavLink>
            )}
        </div>

        {/* The content of the selected tab will be rendered here */}
        <Outlet />
      </main>

      <MobileBottomNav currentUser={currentUser} />
    </div>
  );
}


// This is the main App component that defines all the application routes.
export default function App() {
  return (
    <ThemeProvider>
    <LanguageProvider>
      <AuthProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            success: {
              style: {
                background: '#D1FAE5', // green-100
                color: '#065F46', // green-800
              },
            },
            error: {
              style: {
                background: '#FEE2E2', // red-100
                color: '#991B1B', // red-800
              },
            },
          }}
        />
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/student-login" element={<StudentLoginPage />} />

            {/* Staff Private Routes - All staff pages are nested here */}
            <Route element={<StaffLayoutWrapper />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/leave-requests" element={<LeaveRequestsPage />} />
              <Route path="/classes" element={<ClassesPage />} />
              <Route path="/classes/:classId" element={<ClassDetailPage />} />
              <Route path="/students" element={<StudentsPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/expenses" element={<ExpensesPageWrapper />} />
              <Route path="/payroll" element={<PayrollPage />} />
              <Route path="/ledger" element={<LedgerPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin/rooms" element={<RoomsPage />} />
            </Route>

            {/* Student Private Routes - All student pages are nested here */}
            <Route path="/student" element={<StudentLayoutWrapper />}>
              <Route path="dashboard" element={<StudentDashboardPage />} />
              <Route path="leave" element={<StudentLeavePage />} />
              <Route path="materials" element={<StudentMaterialsPage />} />
              <Route path="payments" element={<StudentPaymentsPage />} />
              <Route path="attendance" element={<StudentAttendanceHistoryPage />} />
              <Route path="profile" element={<StudentProfilePage />} />
              <Route index element={<Navigate to="dashboard" />} />
            </Route>

            {/* A final catch-all to redirect any unknown path */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}